'use client';

import { useEffect, useRef, useState } from 'react';
import BlurImage from '@/components/BlurImage';
import { Instagram } from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { HomeSectionGallery } from '@/lib/cms';
const { colors, fonts } = siteTheme;

const pad = 'clamp(2rem, 6vw, 8rem)';

export default function GallerySection({ data }: { data: HomeSectionGallery }) {
  const IMAGES          = data.images.map((img) => img.url);
  const title           = data.title;
  const instagramHandle = data.instagram_handle;

  /* Triplicate for seamless infinite loop */
  const TRACK = [...IMAGES, ...IMAGES, ...IMAGES];

  const trackRef  = useRef<HTMLDivElement>(null);
  const indexRef  = useRef(IMAGES.length);
  const [offset, setOffset] = useState(0);
  const [transit, setTransit] = useState(false);

  /* Read the actual rendered card width (CSS controls how many are visible) */
  const cardWidth = () => {
    const el = trackRef.current;
    if (!el) return 0;
    const first = el.firstElementChild as HTMLElement | null;
    return first ? first.offsetWidth : 0;
  };

  const goTo = (idx: number, animate: boolean) => {
    setTransit(animate);
    setOffset(idx * cardWidth());
    indexRef.current = idx;
  };

  /* Init + auto-play */
  useEffect(() => {
    goTo(IMAGES.length, false);

    const interval = setInterval(() => {
      goTo(indexRef.current + 1, true);
    }, 3000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Seamless reset at boundaries */
  const handleTransitionEnd = () => {
    const i = indexRef.current;
    if (i >= IMAGES.length * 2) goTo(IMAGES.length, false);
    else if (i < IMAGES.length)  goTo(IMAGES.length * 2 - 1, false);
  };

  /* Recalc on resize */
  useEffect(() => {
    const handler = () => goTo(indexRef.current, false);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section style={data.layout === 'static-grid' ? {} : { backgroundColor: colors.bgAlt, paddingTop: 'clamp(5rem, 7vw, 8rem)', paddingBottom: 'clamp(5rem, 7vw, 8rem)' }}>

      {/* ── Header ── */}
      {data.layout !== 'static-grid' && (
        <div style={{
          padding: `0 ${pad}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'clamp(3rem, 4vw, 5rem)',
          flexWrap: 'wrap',
          gap: '1.6rem',
        }}>
          <h2 style={{
            fontFamily: fonts.sans, fontWeight: 400,
            fontSize: 'clamp(1.4rem, 1.4vw, 1.5rem)',
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: colors.gold,
          }}>
            {title}
          </h2>
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
              fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: colors.goldLight, textDecoration: 'none',
            }}
          >
            {instagramHandle}
            <Instagram size={18} strokeWidth={1.5} />
          </a>
        </div>
      )}

      {data.layout === 'static-grid' ? (
        /* ── Static Grid (4 columns, edge-to-edge) ── */
        <div className="grid grid-cols-2 md:grid-cols-4 w-full">
          {data.images.map((img, i) => (
            <div key={i} className="relative w-full aspect-square overflow-hidden">
              <BlurImage
                src={img.url}
                alt={img.alternativeText || "Sandy Resort Gallery"}
                fill
                className="object-cover transition-transform duration-700 hover:scale-105"
                sizes="(max-width: 768px) 50vw, 25vw"
              />
            </div>
          ))}
        </div>
      ) : (
        /* ── Carousel track ── */
        <div style={{ overflow: 'hidden' }}>
          <div
            ref={trackRef}
            onTransitionEnd={handleTransitionEnd}
            style={{
              display: 'flex',
              transform: `translateX(-${offset}px)`,
              transition: transit ? 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
              willChange: 'transform',
            }}
          >
            {TRACK.map((id, i) => (
              <div
                key={i}
                className="gallery-card"
                style={{
                  /* Mobile: 3 visible | Desktop: 5+ via CSS override below */
                  flex: '0 0 calc(100% / 3)',
                  minWidth: 'calc(100% / 3)',
                  padding: '0 0.4rem',
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ position: 'relative', paddingBottom: '90%', overflow: 'hidden' }}>
                  <BlurImage
                    src={id}
                    alt="Sandy Resort"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 33vw, 20vw"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Responsive card width override ── */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media (min-width: 1024px) {
          .gallery-card {
            flex: 0 0 calc(100% / 5.5) !important;
            min-width: calc(100% / 5.5) !important;
          }
        }
      ` }} />

    </section>
  );
}
